import React, { useEffect, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import FormControl from '@material-ui/core/FormControl';
import FormGroup from '@material-ui/core/FormGroup';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import NativeSelect from '@material-ui/core/NativeSelect';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import SettingsIcon from '@material-ui/icons/Settings';
import { DateTimePicker } from '@material-ui/pickers';
import { formatISO, isBefore, isEqual, isValid, parseISO } from 'date-fns';
import MUIDataTable from 'mui-datatables';
import PropTypes from 'prop-types';

import CheckboxWithId from './CheckboxWithId.jsx';
import SearchIcon from '../icons/SearchIcon.jsx';
import TableFooterWithFirstLastButtons from './TableFooterWithFirstLastButtons.jsx';

const useStyles = makeStyles((theme) => ({
  withMargins: {
    margin: theme.spacing(1),
  },
  wideField: {
    minWidth: theme.spacing(60),
  },
  cardFunctionSelect: {
    '&::before': {
      borderBottom: 'none !important',
    },
    '&::after': {
      borderBottom: 'none !important',
    },
    '& select': {
      '&:focus': {
        backgroundColor: 'rgba(0,0,0,0) !important',
      },
    },
    border: '1px solid lightgray',
    height: theme.spacing(7),
    paddingLeft: '0.7em',
  },
  actionButton: {
    margin: theme.spacing(3),
    marginLeft: theme.spacing(-0.5),
  },
  saveButton: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
  },
  dialogWrapper: {
    minHeight: `90vh !important`,
    maxHeight: `90vh !important`,
  },
  container: {
    padding: '0px !important',
    overflow: 'hidden',
  },
  contentArea: {
    height: '60vh',
    overflowY: 'auto',
    padding: theme.spacing(2),
    width: '100%',
  },
  closeButton: {
    float: 'right',
    marginTop: theme.spacing(-0.9),
  },
  closeIcon: {
    width: theme.spacing(3),
    height: theme.spacing(3.5),
    [theme.breakpoints.down('xs')]: {
      width: theme.spacing(2),
      height: theme.spacing(2),
    },
    color: 'white',
  },
  settingsIcon: {
    marginLeft: theme.spacing(1),
  },
  blueMenu: {
    backgroundColor: theme.palette.secondary.main,
    border: `1px solid ${theme.palette.secondary.main}`,
    marginBottom: '0px !important',
  },
  menuButton: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    color: theme.palette.secondary.contrastText,
  },
  activeButton: {
    borderBottom: `1px solid ${theme.palette.secondary.contrastText}`,
  },
  menuButtonGrey: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    color: theme.palette.neutral.dark,
  },
  activeButtonGrey: {
    borderBottom: '1px solid',
  },
  credentialInfo: {
    marginBottom: theme.spacing(2),
  },
  leftRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    [`& fieldset`]: {
      borderRadius: 0,
    },
  },
  rightRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    height: theme.spacing(10.25),
  },
  fieldTab: {
    display: 'flex',
    flexDirection: 'row',
    padding: theme.spacing(1),
    paddingLeft: 0,
  },
  firstColumn: {
    width: '60%',
    display: 'flex',
    flexDirection: 'column',
    alignContent: 'center',
  },
  firstColumnAdvanced: {
    width: '60%',
    display: 'flex',
    flexDirection: 'column',
    alignContent: 'center',
    marginLeft: theme.spacing(1.75),
  },
  secondColumn: {
    width: '40%',
    display: 'flex',
    flexDirection: 'column',
    alignContent: 'center',
    marginTop: theme.spacing(-1.5),
  },
  fieldLabel: {
    minWidth: theme.spacing(15),
  },
  fieldLabelRight: {
    width: theme.spacing(30),
    textAlign: 'right',
  },
  hiddenFields: {
    display: 'none',
  },
  dataSearchWrapper: {
    position: 'relative',
  },
  dataSearch: {
    [theme.breakpoints.up('md')]: {
      position: 'absolute',
      right: theme.spacing(5),
      marginTop: theme.spacing(2),
      zIndex: 2,
      top: '0px',
    },
    [`& fieldset`]: {
      borderRadius: 0,
    },
  },
  dataTable: {
    position: 'relative',
    zIndex: 1,
    boxShadow: 'none',
    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
    borderLeft: '1px solid rgba(0, 0, 0, 0.12)',
    borderRight: '1px solid rgba(0, 0, 0, 0.12)',
  },
}));

const CredentialEdit = ({
  visible,
  credential,
  dismiss,
  onSubmit,
  allAccessGroups,
  allSingleReaderAccessRights,
}) => {
  const classes = useStyles();

  const cardFunctionEnabled = true; // TODO: Set properly when licensing is implemented

  const initialValues = { ...credential };

  /**
   * Helper function to get the index of an accessGroup based on its id property
   * @param {Number} id id of row to get index of
   */
  const findIndexById = (id, list) => {
    for (let i = 0; i < list.length; i += 1) {
      if (list[i].id === id) {
        return i;
      }
    }
    return -1;
  };

  /**
   * Convert array of ag data to array of indeces. Used to populate the data table
   */
  const getAgSelectedIndeces = () => {
    return initialValues.accessGroups.map((ag) => findIndexById(ag.id, allAccessGroups));
  };

  /**
   * Convert array of singleDoorAccess data to array of indeces. Used to populate the data table
   */
  const getSdrSelectedIndeces = () => {
    if (!initialValues.singleReaderAccessRights.engageReaders) {
      return [];
    }
    return initialValues.singleReaderAccessRights.engageReaders.map((door) =>
      findIndexById(door.id, allSingleReaderAccessRights)
    );
  };

  const [fieldsShown, setFieldsShown] = useState('credentialInfo');
  const [tableShown, setTableShown] = useState('ag');
  const [activationDate, setActivationDate] = useState(
    initialValues.activationDate || formatISO(new Date())
  );
  const [activationEnabled, setActivationEnabled] = useState(
    !!initialValues.activationDate || false
  );
  const [deactivationDate, setDeactivationDate] = useState(
    initialValues.deactivationDate || formatISO(new Date())
  );
  const [deactivationEnabled, setDeactivationEnabled] = useState(
    !!initialValues.deactivationDate || false
  );
  const [deactivationError, setDeactivationError] = useState(false);
  const [cardFunction, setCardFunction] = useState(
    cardFunctionEnabled ? initialValues.cardFunction || 'norm' : 'norm'
  );
  const [active, setActive] = useState(initialValues.active);
  const [useAdaTiming, setUseAdaTiming] = useState(initialValues.useAdaTiming || false);
  const [accessGroups, setAccessGroups] = useState(initialValues.accessGroups || []);
  const initialAgRows = initialValues.accessGroups ? getAgSelectedIndeces() : [];
  const [selectedAgRows, setSelectedAgRows] = useState(initialAgRows);
  const [singleReaderAccessRights, setSingleReaderAccessRights] = useState(
    initialValues.singleReaderAccessRights?.engageReaders || []
  );
  const initialSdrRows = initialValues.singleReaderAccessRights ? getSdrSelectedIndeces() : [];
  const [selectedSdrRows, setSelectedSdrRows] = useState(initialSdrRows);
  const [agSearch, setAgSearch] = useState('');
  const [doorSearch, setDoorSearch] = useState('');

  const showDateIsBeforeDateError = (date1, date2) => {
    // only compare at the minute level, since seconds are not rendered in the interface.
    date1.setSeconds(0);
    date2.setSeconds(0);
    if (date1 && date2 && isValid(date1) && isValid(date2)) {
      if (isEqual(date1, date2)) {
        return false;
      }
      return isBefore(date1, date2);
    }
    // TODO: Need to return something in this branch
  };

  const handleDeactivationDateError = () => {
    // check the error if not the activation date is more than the deactivation date
    return deactivationError
      ? 'Deactivation date must be equal to or after the activation date.'
      : null;
  };

  const updateActivationDate = (date) => {
    const isoDate = formatISO(date);
    setActivationDate(isoDate);
    if (activationEnabled && deactivationEnabled) {
      setDeactivationError(
        showDateIsBeforeDateError(parseISO(deactivationDate), parseISO(isoDate))
      );
    }
  };

  const updateDeactivationDate = async (date) => {
    const isoDate = formatISO(date);
    setDeactivationDate(isoDate);
    if (activationEnabled && deactivationEnabled) {
      setDeactivationError(showDateIsBeforeDateError(parseISO(isoDate), parseISO(activationDate)));
    }
  };

  const updateCardFunction = (event) => {
    setCardFunction(event.target.value);
  };

  const updateActive = (event) => {
    setActive(event.target.checked);
  };

  const updateActivationEnabled = (event) => {
    setActivationEnabled(event.target.checked);
    setActivationDate(formatISO(new Date()));
    if (event.target.checked && deactivationEnabled) {
      setDeactivationError(
        showDateIsBeforeDateError(parseISO(deactivationDate), parseISO(activationDate))
      );
    } else {
      setDeactivationError(false);
    }
  };

  const updateDeactivationEnabled = (event) => {
    setDeactivationEnabled(event.target.checked);
    setDeactivationDate(formatISO(new Date()));
    if (activationEnabled && event.target.checked) {
      setDeactivationError(
        showDateIsBeforeDateError(parseISO(deactivationDate), parseISO(activationDate))
      );
    } else {
      setDeactivationError(false);
    }
  };

  const updateUseAdaTiming = (event) => {
    setUseAdaTiming(event.target.checked);
  };

  const updateAgSearch = (event) => setAgSearch(event.target.value);
  const updateDoorSearch = (event) => setDoorSearch(event.target.value);

  /**
   * update 'initialValues' when the card changes
   */
  useEffect(() => {
    if (visible) {
      setActive(initialValues.active);
      setAccessGroups(initialValues.accessGroups || []);
      setActivationDate(initialValues.activationDate || formatISO(new Date()));
      setDeactivationDate(initialValues.deactivationDate || formatISO(new Date()));

      setActivationEnabled(!!initialValues.activationDate);
      setDeactivationEnabled(!!initialValues.deactivationDate);

      if (initialValues.accessGroups && initialValues.accessGroups !== []) {
        const selectedRowIndeces = getAgSelectedIndeces();
        setSelectedAgRows(selectedRowIndeces);
      }
      if (
        initialValues.singleReaderAccessRights &&
        initialValues.singleReaderAccessRights.engageReaders &&
        initialValues.singleReaderAccessRights.engageReaders !== []
      ) {
        const selectedRowIndeces = getSdrSelectedIndeces();
        setSelectedSdrRows(selectedRowIndeces);
      }
      setCardFunction(initialValues.cardFunction || 'norm');
      setUseAdaTiming(initialValues.useAdaTiming || false);
      showAG();
      showCredentialInfo();
    }
  }, [visible]);

  const agColumns = ['Access Groups'];

  const agData = allAccessGroups.map((ag) => {
    return [ag.name];
  });

  const singleDoorColumns = ['Doors'];

  const singleDoorData = allSingleReaderAccessRights.map((door) => {
    return [door.name];
  });

  const handleAgSelected = (currentRowSelected, allRowsSelected) => {
    if (allRowsSelected.length === 0) {
      setSelectedAgRows([]);
      setAccessGroups([]);
    }
    const updatedSelectedAgRows = [];
    const selectedGroups = allRowsSelected.map((selectedRow) => {
      updatedSelectedAgRows.push(selectedRow.dataIndex);
      setSelectedAgRows(updatedSelectedAgRows);
      return allAccessGroups[selectedRow.dataIndex];
    });
    setAccessGroups(selectedGroups);
  };

  const handleSdrSelected = (currentRowSelected, allRowsSelected) => {
    if (allRowsSelected.length === 0) {
      setSelectedSdrRows([]);
      setSingleReaderAccessRights({ engageReaders: [] });
    }
    const updatedSelectedSdrRows = [];
    const selectedSdr = allRowsSelected.map((selectedRow) => {
      updatedSelectedSdrRows.push(selectedRow.dataIndex);
      setSelectedSdrRows(updatedSelectedSdrRows);
      return allSingleReaderAccessRights[selectedRow.dataIndex];
    });
    setSingleReaderAccessRights({ engageReaders: selectedSdr });
  };

  const baseTableOptions = {
    filter: false,
    print: false,
    search: false,
    download: false,
    viewColumns: false,
    showFirstButton: true,
    showLastButton: false,
    rowsPerPage: 5,
    rowsPerPageOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    customFooter: (count, page, rowsPerPage, changeRowsPerPage, changePage, textLabels) => {
      return (
        <TableFooterWithFirstLastButtons
          count={count}
          page={page}
          rowsPerPage={rowsPerPage}
          changeRowsPerPage={changeRowsPerPage}
          changePage={changePage}
          textLabels={textLabels}
        />
      );
    },
  };

  const agOptions = {
    ...baseTableOptions,
    onRowSelectionChange: handleAgSelected,
    rowsSelected: selectedAgRows,
    searchText: agSearch,
  };

  const sdrOptions = {
    ...baseTableOptions,
    onRowSelectionChange: handleSdrSelected,
    rowsSelected: selectedSdrRows,
    searchText: doorSearch,
  };

  const handleSubmit = () => {
    dismiss();
    let activationDateValue = activationDate;
    let deactivationDateValue = deactivationDate;
    if (!activationEnabled) {
      activationDateValue = null;
    }
    if (!deactivationEnabled) {
      deactivationDateValue = null;
    }
    onSubmit({
      id: initialValues.id,
      credentialNumber: initialValues.credentialNumber,
      activationDate: activationDateValue,
      deactivationDate: deactivationDateValue,
      cardFunction,
      active,
      useAdaTiming,
      accessGroups,
      singleReaderAccessRights,
    });
  };

  const generateFunctionOptions = () => {
    const cardFunctionOptions = [
      { id: 'norm', name: 'Normal' },
      { id: 'toggle', name: 'Toggle' },
      { id: 'freeze', name: 'Freeze' },
      { id: 'lockDown', name: 'Lockdown' },
      { id: 'passThru', name: 'Pass Through' },
      { id: 'oneTm', name: 'One Time Use' },
      { id: 'delAlrm', name: 'Delete With Alarm' },
      { id: 'ctMnAux', name: 'Main and Aux Unlock' },
      { id: 'ctAux', name: 'Aux Unlock' },
      { id: 'ctMnAuxPassThru', name: 'Main and Aux Pass Through' },
      { id: 'ctMnAuxToggle', name: 'Main and Aux Toggle' },
      { id: 'updtFrmSvr', name: 'Update From Server' },
      { id: 'ctAuxToggle', name: 'Aux Toggle' },
      { id: 'updtFrmSvrNrm', name: 'Update From Server' },
    ];

    return cardFunctionEnabled
      ? cardFunctionOptions.map((option) => (
          <option key={option.id} value={option.id}>
            {option.name}
          </option>
        ))
      : [];
  };

  const showCredentialInfo = () => {
    setFieldsShown('credentialInfo');
  };

  const showAdvanced = () => {
    setFieldsShown('advanced');
  };

  const showSDR = () => {
    setTableShown('sdr');
  };

  const showAG = () => {
    setTableShown('ag');
  };

  const formRef = useRef();

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={visible}
      className={classes.dialogWrapper}
      onClose={() => {
        dismiss();
      }}
    >
      <form data-testid="credentialForm" ref={formRef}>
        <DialogContent className={classes.container}>
          <div className={`${classes.blueMenu} ${classes.credentialInfo}`}>
            <Button
              className={`${classes.menuButton} ${
                fieldsShown === 'credentialInfo' ? classes.activeButton : ''
              }`}
              onClick={showCredentialInfo}
            >
              Credential Information
            </Button>
            <Button
              className={`${classes.menuButton} ${
                fieldsShown === 'advanced' ? classes.activeButton : ''
              }`}
              onClick={showAdvanced}
            >
              Advanced
              <SettingsIcon className={classes.settingsIcon} />
            </Button>
            <IconButton
              className={classes.closeButton}
              disableRipple
              onClick={() => {
                dismiss();
              }}
              data-testid="x-button"
            >
              <CloseIcon className={classes.closeIcon} />
            </IconButton>
          </div>

          <div className={classes.contentArea}>
            {fieldsShown === 'advanced' && (
              <div>
                <Button className={`${classes.menuButtonGrey} ${classes.activeButtonGrey}`}>
                  Credential Properties
                </Button>
              </div>
            )}
            <FormGroup>
              <div>
                <div
                  className={`${classes.fieldTab} ${
                    fieldsShown !== 'credentialInfo' ? classes.hiddenFields : ''
                  }`}
                >
                  <div className={classes.firstColumn}>
                    <FormControl className={classes.leftRow}>
                      <Typography id="credentialNumberLabel" className={classes.fieldLabel}>
                        Credential Number
                      </Typography>
                      <TextField
                        className={`${classes.withMargins} ${classes.wideField}`}
                        value={initialValues.credentialNumber}
                        variant="outlined"
                        disabled
                        required
                        inputProps={{
                          'aria-labelledby': 'credentialNumberLabel',
                        }}
                      />
                    </FormControl>
                    <FormControl className={classes.leftRow}>
                      <Typography id="activationDateLabel" className={classes.fieldLabel}>
                        Activation Date
                      </Typography>
                      <Checkbox
                        checked={activationEnabled}
                        onChange={updateActivationEnabled}
                        name="enableActivation"
                        inputProps={{
                          'aria-label': 'Enable Activation Date',
                        }}
                      />
                      <DateTimePicker
                        className={`${classes.withMargins} ${classes.wideField}`}
                        disabled={!activationEnabled}
                        inputVariant="outlined"
                        format="PPPp"
                        value={activationDate}
                        onChange={updateActivationDate}
                        inputProps={{
                          'aria-labelledby': 'activationDateLabel',
                        }}
                      />
                    </FormControl>
                    <FormControl className={classes.leftRow}>
                      <Typography id="deactivationDateLabel" className={classes.fieldLabel}>
                        Deactivation Date
                      </Typography>
                      <Checkbox
                        checked={deactivationEnabled}
                        onChange={updateDeactivationEnabled}
                        name="enableDeactivation"
                        inputProps={{
                          'aria-label': 'Enable Deactivation Date',
                        }}
                      />
                      <DateTimePicker
                        className={`${classes.withMargins} ${classes.wideField}`}
                        disabled={!deactivationEnabled}
                        inputVariant="outlined"
                        format="PPPp"
                        value={deactivationDate}
                        onChange={updateDeactivationDate}
                        error={deactivationError}
                        helperText={handleDeactivationDateError()}
                        inputProps={{
                          'aria-labelledby': 'deactivationDateLabel',
                        }}
                      />
                    </FormControl>
                  </div>
                  <div className={classes.secondColumn}>
                    <div className={classes.rightRow}>
                      <Typography id="activeLabel" className={classes.fieldLabelRight}>
                        Active
                      </Typography>
                      <FormControl>
                        <Checkbox
                          checked={active}
                          onChange={updateActive}
                          name="active"
                          inputProps={{
                            'aria-labelledby': 'activeLabel',
                          }}
                        />
                      </FormControl>
                    </div>
                    <div className={classes.rightRow}>
                      <Typography id="adaLabel" className={classes.fieldLabelRight}>
                        Use ADA Timing
                      </Typography>
                      <FormControl>
                        <Checkbox
                          checked={useAdaTiming}
                          onChange={updateUseAdaTiming}
                          name="useAdaTiming"
                          inputProps={{
                            'aria-labelledby': 'adaLabel',
                          }}
                        />
                      </FormControl>
                    </div>
                  </div>
                </div>
                <div
                  className={`${classes.fieldTab} ${
                    fieldsShown !== 'advanced' ? classes.hiddenFields : ''
                  }`}
                >
                  <div className={classes.firstColumnAdvanced}>
                    <FormControl className={classes.leftRow}>
                      <div className={classes.fieldLabel}>
                        <Typography id="issueCodeLabel" display="inline">
                          Issue Code
                        </Typography>
                      </div>
                      <TextField
                        className={`${classes.withMargins} ${classes.wideField}`}
                        disabled
                        value={initialValues.issueCode}
                        variant="outlined"
                        inputProps={{
                          'aria-labelledby': 'issueCodeLabel',
                        }}
                      />
                    </FormControl>
                    <FormControl className={classes.leftRow}>
                      <Typography id="cardFunctionLabel" className={classes.fieldLabel}>
                        Card Function
                      </Typography>
                      <NativeSelect
                        className={`${classes.withMargins} ${classes.wideField} ${classes.cardFunctionSelect}`}
                        disabled={!cardFunctionEnabled}
                        value={cardFunction}
                        onChange={updateCardFunction}
                        inputProps={{
                          'aria-labelledby': 'cardFunctionlabel',
                        }}
                      >
                        {generateFunctionOptions()}
                      </NativeSelect>
                    </FormControl>
                  </div>
                </div>
              </div>
            </FormGroup>
            {fieldsShown === 'credentialInfo' && (
              <div className={classes.blueMenu}>
                <Button
                  className={`${classes.menuButton} ${
                    tableShown === 'ag' ? classes.activeButton : ''
                  }`}
                  onClick={showAG}
                >
                  Access Groups
                </Button>
                <Button
                  className={`${classes.menuButton} ${
                    tableShown === 'sdr' ? classes.activeButton : ''
                  }`}
                  onClick={showSDR}
                >
                  Single Door Rights
                </Button>
              </div>
            )}
            {tableShown === 'ag' && fieldsShown === 'credentialInfo' && (
              <div className={classes.dataSearchWrapper}>
                <TextField
                  className={`${classes.wideField} ${classes.dataSearch}`}
                  onChange={updateAgSearch}
                  value={agSearch}
                  variant="outlined"
                  placeholder="Search"
                  size="small"
                  data-testid="agSearch"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                <MUIDataTable
                  data={agData}
                  columns={agColumns}
                  options={agOptions}
                  components={{ Checkbox: CheckboxWithId }}
                  className={classes.dataTable}
                />
              </div>
            )}
            {tableShown === 'sdr' && fieldsShown === 'credentialInfo' && (
              <div className={classes.dataSearchWrapper}>
                <TextField
                  className={`${classes.wideField} ${classes.dataSearch}`}
                  onChange={updateDoorSearch}
                  value={doorSearch}
                  variant="outlined"
                  placeholder="Search"
                  size="small"
                  data-testid="doorSearch"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                <MUIDataTable
                  data={singleDoorData}
                  columns={singleDoorColumns}
                  options={sdrOptions}
                  components={{ Checkbox: CheckboxWithId }}
                  className={classes.dataTable}
                />
              </div>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <div className={classes.actionButtons}>
            <Button
              className={classes.actionButton}
              onClick={() => {
                dismiss();
              }}
            >
              Cancel
            </Button>
            <Button
              className={`${classes.actionButton} ${classes.saveButton}`}
              variant="contained"
              type="button"
              onClick={() => {
                handleSubmit();
                formRef.current.reportValidity();
              }}
            >
              Save
            </Button>
          </div>
        </DialogActions>
      </form>
    </Dialog>
  );
};

CredentialEdit.propTypes = {
  visible: PropTypes.bool.isRequired,
  dismiss: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default CredentialEdit;
